import ENV from 'config/env.conf';

// makes tests not fail
let globalThis;

// read through global, not directly imported, to reduce circular imports problems
const flags = () => ((window && window.SLICK) || (global && global.SLICK) || (globalThis && globalThis.SLICK))?.flag;

const fromEnvToCutoff = (env) =>
  'production' === env?.NODE_ENV &&
  'staging' !== env?.REACT_APP_ENV &&
  'alpha' !== env?.REACT_APP_ENV &&
  'beta' !== env?.REACT_APP_ENV
    ? 7
    : 5;

export const isAllowed = (options) => {
  const flag = options?.flag;
  const level = options?.level ?? 0;
  const cutoff = fromEnvToCutoff(options?.env);

  if ('' === flag || void 1 === flag || null === flag) return level >= cutoff;

  if (true === flag || false === flag) return flag;
  if ('false' === flag || '0' === flag || 0 === flag) return false;
  if ('true' === flag || '10' === flag || 10 === flag) return true;

  const int = Number.parseInt(flag);
  return Number.isSafeInteger(int) ? level >= int : level >= cutoff;
};

const create =
  ({ tag, method, icon, level }) =>
  (...args) => {
    if (!isAllowed({ level, env: ENV, flag: flags()?.overrideLogLevel })) return args.pop();

    // eslint-disable-next-line no-console
    console[method](icon, tag, ...args);

    return args.pop();
  };

// noinspection SpellCheckingInspection
const unignore =
  ({ tag, level, icon }) =>
  (...args) => {
    const promise = args.pop();

    if (!isAllowed({ level, env: ENV, flag: flags()?.overrideLogLevel })) return promise;

    if (!(promise instanceof Promise)) {
      console.error(icon, tag, 'Last argument is not a promise:', ...args);
      return Promise.resolve(promise);
    }

    promise.catch(
      (reason) => void console.warn(icon, tag, ...args, reason), // eslint-disable-line no-console
    );

    return promise;
  };

const wrap =
  ({ tag, level, icon }) =>
  (...extra) => {
    const fn = extra.pop();

    if (!isAllowed({ level, env: ENV, flag: flags()?.overrideLogLevel }) && !flags()?.loggerWrap) return fn;

    // noinspection UnnecessaryLocalVariableJS
    const wrapper = (...args) => {
      try {
        const result = fn(...args);

        console.log(icon, tag, ...extra, '📞 (', ...args, ') ->', result); // eslint-disable-line no-console
        return result;
      } catch (error) {
        console.log(icon, tag, ...extra, '💣 (', ...args, ') -<', error); // eslint-disable-line no-console
        throw error;
      }
    };
    Object.defineProperty(wrapper, 'name', { value: `wrapped(${String(fn?.name ?? '') || 'unknown'})` });

    return wrapper;
  };

const O = (object) => {
  const array = Object.entries(object ?? {}).flatMap(([k, v]) => [`${k}:`, v]);
  array.unshift('«');
  array.push('»');
  return array;
};

const filterEmptyStringOut = ($) => $;

const fromAnyToTag = (value) => {
  if (value === null || undefined === value) return '';
  const stringified = String(value).trim();
  return stringified ? `[${stringified}]` : '';
};

// NOTE: this little JSDoc snippet seems to stop WebStorm adding a very long inlay hint
/**
 * @typedef {{
 *   stack: Function,
 *   trace: Function,
 *   debug: Function,
 *   info: Function,
 *   warn: Function,
 *   error: Function,
 *   unignore: Function,
 *   wrap: Function
 * }} SlickLogger
 *
 * @param tag
 * @return SlickLogger
 * */
const logger = (tag) => {
  tag = Array.isArray(tag) ? tag.map(fromAnyToTag).filter(filterEmptyStringOut).join(' ') : fromAnyToTag(tag);

  // noinspection SpellCheckingInspection
  return {
    //
    // general log functions for common use cases
    error: create({ tag, method: 'error', icon: '[E]', level: 9 }),
    warn: create({ tag, method: 'warn', icon: '[W]', level: 8 }),
    info: create({ tag, method: 'info', icon: '[I]', level: 7 }),
    debug: create({ tag, method: 'log', icon: '[D]', level: 5 }),
    trace: create({ tag, method: 'log', icon: '[T]', level: 3 }),
    stack: create({ tag, method: 'trace', icon: '[S]', level: 2 }),

    //
    // specialized log functions for specific use cases
    unignore: unignore({ tag, icon: '[U]', level: 8 }),
    wrap: wrap({ tag, icon: '[F]', level: 3 }),

    //
    // specialized function for converting key/val object into a flat key,val,key,val array
    O,
  };
};

logger.O = O;

(() => {
  const loop = () => {
    if (flags()) {
      const flag = flags()?.overrideLogLevel;
      console.info('[I]', '[LOGGER]', '[INIT-RELATED]', ...O({ flag }));
    } else {
      return setTimeout(loop, 1000);
    }
  };

  loop();
})();

// noinspection JSUnusedGlobalSymbols
export default logger;
